<script>
import AkLabel from '@components/v2/general/AkLabel';
import InputNumber from '@components/input/InputNumber';
import utilsMixin from '../../../mixins/utilsMixin';
import helperMixin from '../../../mixins/helperMixin';

export default {
  components: {AkLabel, InputNumber},
  mixins: [utilsMixin, helperMixin],
  inject: {pSubmitted: {default: null}, pDisabled: {default: null}, pIgnoredValidator: {default: null}},
  props: {
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
      default: undefined,
    },
    displayLabel: {
      type: Boolean,
      required: false,
      default: true,
    },
    modelValue: {
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: undefined,
    },
    className: {
      type: String,
      required: false,
      default: '',
    },
    inputClass: {
      type: String,
      required: false,
      default: '',
    },
    validator: {
      type: Object,
      required: false,
      default: undefined,
    },
    submitted: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    mode: {
      type: String,
      required: false,
    },
    prefix: {
      type: String,
      required: false,
      default: undefined,
    },
    suffix: {
      type: String,
      required: false,
      default: undefined,
    },
    min: {
      type: Number,
      required: false,
      default: null,
    },
    max: {
      type: Number,
      required: false,
      default: null,
    },
    fractionDigits: {
      type: Number,
      required: false,
      default: 2,
    },
    ignoredValidator: {
      type: Array,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    required: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    hideRequired: {
      type: Boolean,
      required: false,
      default: false,
    },
    information: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    currentValue: {
      get: function () {
        return this.modelValue;
      },
      set: function (val) {
        this.$emit('update:modelValue', val);
      },
    },
    isInline() {
      return this.inline;
    },
    getPlaceholder() {
      return this.placeholder ? this.placeholder : this.label;
    },
    alreadySubmitted() {
      if (this.submitted !== undefined) return this.submitted;
      if (this.pSubmitted !== undefined) return this.pSubmitted;
      return false;
    },
    isDisabled() {
      if (this.disabled !== undefined) return this.disabled;
      if (this.pDisabled !== undefined) return this.pDisabled;
      return false;
    },
    hasValidator() {
      return this.validator !== undefined;
    },
    isRequired() {
      return (
        this.hasValidator &&
        this.validator.required !== undefined &&
        (this.validator.required.$params === undefined ||
          this.validator.required.$params.type === 'required' ||
          (this.validator.required.$params.type === 'requiredIf' && this.validator.required.$params.prop === true))
      );
    },
    getIgnoredValidator() {
      if (this.ignoredValidator != undefined) return this.ignoredValidator;
      if (this.pIgnoredValidator != undefined) return this.pIgnoredValidator;
      return [];
    },
    isInvalid() {
      if (this.hasValidator && this.getIgnoredValidator.length > 0) {
        for (let error of this.validator.$errors) {
          if (this.getIgnoredValidator.indexOf(error.$validator) === -1) {
            return true;
          }
        }
      } else {
        return this.hasValidator && this.validator.$invalid;
      }
      return false;
    },
    isRequiredFailed() {
      return (
        this.isRequired &&
        this.getIgnoredValidator.indexOf('required') === -1 &&
        (this.validator.required.$invalid || (!this.validator.required && this.validator.$invalid)) &&
        this.alreadySubmitted
      );
    },
    getRequiredMessage() {
      if (this.isRequired && this.isRequiredFailed) {
        if (this.isCustomMessage(this.validator.required)) {
          return this.validator.required.$message;
        } else {
          return this.$t('field_required', [this.label]);
        }
      }
      return '';
    },
    isBoundaryRequired() {
      return (
        this.validator !== undefined &&
        this.validator.betweenValue !== undefined &&
        this.validator.betweenValue.$invalid &&
        this.alreadySubmitted
      );
    },
    isMinValueRequired() {
      return (
        this.validator !== undefined &&
        this.validator.minValue !== undefined &&
        this.validator.minValue.$invalid &&
        this.alreadySubmitted
      );
    },
    isMaxValueRequired() {
      return (
        this.validator !== undefined &&
        this.validator.maxValue !== undefined &&
        this.validator.maxValue.$invalid &&
        this.alreadySubmitted
      );
    },
    getCurrency() {
      return this.mode === 'currency' ? 'EUR' : undefined;
    },
    useGrouping() {
      return this.mode === 'currency';
    },
  },
  methods: {
    ensureTwoDecimals() {
      if (this.currentValue !== null && this.currentValue !== undefined) {
        this.currentValue = parseFloat(this.currentValue);
      }
    },
  },
};
</script>

<template>
  <div class="w-full input-layout form-control" :class="[className]">
    <AkLabel
      v-if="!isInline && displayLabel"
      :required="!hideRequired && (this.isRequired || this.required === true)"
      :information="this.information">
      {{ label }}
    </AkLabel>
    <div class="relative">
      <InputNumber
        ref="input"
        @blur="ensureTwoDecimals"
        v-model="currentValue"
        :class="[{'p-invalid': isInvalid && alreadySubmitted, disabled: isDisabled, 'has-suffix': suffix}]"
        :currency="getCurrency"
        :input-props="{inputmode: 'decimal'}"
        :disabled="isDisabled"
        class="w-full text-grey-900 text-base font-inter form-control"
        :max="max"
        :max-fraction-digits="fractionDigits"
        :min="min"
        :inputClass="inputClass"
        :min-fraction-digits="0"
        :mode="mode"
        :placeholder="getPlaceholder"
        :prefix="prefix"
        :use-grouping="useGrouping"
        type="text" />
      <span v-if="suffix" class="text-grey-500 absolute leading-[52px] top-0 right-4">{{ suffix }}</span>
    </div>

    <small v-if="isRequiredFailed" class="p-error">{{ this.getRequiredMessage }}</small>

    <small v-if="isBoundaryRequired" class="p-error">
      {{
        $t('min_max_value', [
          label,
          useGrouping ? this.formatCurrencyEUR(validator.minValue.$params.min) : validator.minValue.$params.min,
          useGrouping ? this.formatCurrencyEUR(validator.maxValue.$params.max) : validator.maxValue.$params.max,
        ])
      }}
    </small>

    <small v-if="isMinValueRequired" class="p-error">
      {{
        $t('min_value', [
          label,
          useGrouping ? this.formatCurrencyEUR(validator.minValue.$params.min) : validator.minValue.$params.min,
        ])
      }}
    </small>
    <small v-if="isMaxValueRequired" class="p-error">
      {{
        $t('max_value', [
          label,
          useGrouping ? this.formatCurrencyEUR(validator.maxValue.$params.max) : validator.maxValue.$params.max,
        ])
      }}
    </small>
  </div>
</template>
